import { FormGroup } from '@angular/forms';
import type { Form, FormField } from '../interfaces/form.interface';
import type { DropdownData } from '../interfaces/input.interface';
import type { ReactiveFieldLoadState, ReactiveFieldRuntimeState } from '../interfaces/reactive-field-runtime-state.interface';
import { ReactiveDropdownOption, ReactiveFormValueUtils } from './reactive-form-value.utils';

/** Builds immutable runtime snapshots for every field without mutating FormField definitions. */
export class ReactiveFieldRuntimeStateManager {
    public static build<T>(
        form: Form<T>,
        model: FormGroup | null,
        dropdownsFinalized: boolean,
        fieldLoadStates: ReadonlyMap<string, ReactiveFieldLoadState> = new Map(),
        fieldErrors: ReadonlyMap<string, string> = new Map(),
    ): Map<string, ReactiveFieldRuntimeState<T>> {
        const states = new Map<string, ReactiveFieldRuntimeState<T>>();

        for (const section of form.sections ?? []) {
            for (const field of section.fields ?? []) {
                if (!field.name) {
                    continue;
                }

                states.set(
                    field.name,
                    this.createFieldState(field, model, form.type, dropdownsFinalized, fieldLoadStates, fieldErrors),
                );
            }
        }

        return states;
    }

    public static isFieldReady<T>(
        field: FormField<T>,
        states: ReadonlyMap<string, ReactiveFieldRuntimeState<T>>,
        formType: number,
        dropdownsFinalized: boolean,
    ): boolean {
        const state = field.name ? states.get(field.name) : undefined;

        if (state) {
            return state.loadState === 'idle' || state.loadState === 'ready' || state.loadState === 'empty';
        }

        return ReactiveFormValueUtils.isFieldReady(field, formType, dropdownsFinalized);
    }

    public static hasLoadingDropdowns<T>(states: ReadonlyMap<string, ReactiveFieldRuntimeState<T>>): boolean {
        for (const state of states.values()) {
            if (state.loadState === 'loading') {
                return true;
            }
        }

        return false;
    }

    private static createFieldState<T>(
        field: FormField<T>,
        model: FormGroup | null,
        formType: number,
        dropdownsFinalized: boolean,
        fieldLoadStates: ReadonlyMap<string, ReactiveFieldLoadState>,
        fieldErrors: ReadonlyMap<string, string>,
    ): ReactiveFieldRuntimeState<T> {
        const dropdownData = field.input?.data as DropdownData<unknown> | undefined;
        const values = this.toOptions(dropdownData?.values);
        const filteredValues = this.toOptions(dropdownData?.filtered);
        const options = filteredValues.length > 0 ? filteredValues : values;
        const primaryKey = ReactiveFormValueUtils.getFieldPrimaryKey(field);
        const optionsById = ReactiveFormValueUtils.createOptionsIndex(options, primaryKey);
        const controlValue = field.name ? model?.get(field.name)?.value : null;

        return {
            definition: field,
            values,
            filteredValues,
            selectedValues: this.resolveSelectedValues(dropdownData?.selected, optionsById, primaryKey),
            pendingSelectedIds: this.resolvePendingSelectedIds(controlValue, optionsById, primaryKey),
            loadState: this.resolveLoadState(field, formType, dropdownsFinalized, fieldLoadStates),
            errorMessage: field.name ? fieldErrors.get(field.name) ?? null : null,
            optionsById,
        };
    }

    private static resolveLoadState<T>(
        field: FormField<T>,
        formType: number,
        dropdownsFinalized: boolean,
        fieldLoadStates: ReadonlyMap<string, ReactiveFieldLoadState>,
    ): ReactiveFieldLoadState {
        const fieldName = field.name ?? '';
        const explicitState = fieldName ? fieldLoadStates.get(fieldName) : undefined;

        if (explicitState === 'loading' || explicitState === 'error') {
            return explicitState;
        }

        const inferredState = ReactiveFormValueUtils.getDropdownLoadState(field, formType, dropdownsFinalized);

        return explicitState ?? inferredState;
    }

    private static toOptions(values: unknown): ReactiveDropdownOption[] {
        if (!Array.isArray(values)) {
            return [];
        }

        return values
            .map((value) => this.toOption(value))
            .filter((value): value is ReactiveDropdownOption => value !== null);
    }

    private static toOption(value: unknown): ReactiveDropdownOption | null {
        if (value === null || value === undefined) {
            return null;
        }

        if (typeof value === 'object') {
            const record = value as Partial<ReactiveDropdownOption> & Record<string, unknown>;

            if ('value' in record) {
                return {
                    label: record.label ?? '',
                    value: record.value,
                };
            }
        }

        return { value };
    }

    private static resolveSelectedValues(
        selected: unknown,
        optionsById: ReadonlyMap<string, ReactiveDropdownOption>,
        primaryKey: string,
    ): ReactiveDropdownOption[] {
        const selectedValues = Array.isArray(selected) ? selected : [selected];
        const resolvedValues: ReactiveDropdownOption[] = [];

        for (const selectedValue of selectedValues) {
            if (!ReactiveFormValueUtils.hasNonEmptyValue(selectedValue)) {
                continue;
            }

            const id = ReactiveFormValueUtils.extractComparableId(selectedValue, primaryKey);
            const option = id === null ? null : optionsById.get(String(id));

            resolvedValues.push(option ?? { value: selectedValue });
        }

        return resolvedValues;
    }

    private static resolvePendingSelectedIds(
        controlValue: unknown,
        optionsById: ReadonlyMap<string, ReactiveDropdownOption>,
        primaryKey: string,
    ): Array<string | number> {
        const values = Array.isArray(controlValue) ? controlValue : [controlValue];
        const pendingIds: Array<string | number> = [];

        for (const value of values) {
            const id = ReactiveFormValueUtils.extractComparableId(value, primaryKey);

            if (id !== null && !optionsById.has(String(id))) {
                pendingIds.push(id);
            }
        }

        return pendingIds;
    }
}
